import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useRecommendation } from '../context/RecommendationContext';
import { downloadJson, generateDebugBundleFilename } from '../utils/debug/downloadJson';
import { buildDebugBundle } from '../utils/debug/buildDebugBundle';
import { shouldShowDebugBundle, enableDebugBundle, disableDebugBundle } from '../utils/debug/debugBundleHelpers';

// Build fingerprint for deployment verification
// This gets replaced at build time by Vite's define plugin
const BUILD_COMMIT = import.meta.env.VITE_GIT_COMMIT || 'dev';
const BUILD_TIME = import.meta.env.VITE_BUILD_TIME || new Date().toISOString();

interface DebugBundleButtonProps {
  className?: string;
  style?: React.CSSProperties;
}

export function DebugBundleButton({ className = '', style = {} }: DebugBundleButtonProps) {
  const location = useLocation();
  const { collectedData, recommendationResult, convertToSystemRecInput } = useRecommendation();
  const [notes, setNotes] = useState('');
  const [showNotes, setShowNotes] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [devToolsExpanded, setDevToolsExpanded] = useState(false);

  // Check if full debug mode is enabled (for advanced features)
  const searchParams = new URLSearchParams(location.search);
  const isFullDebugMode = shouldShowDebugBundle(searchParams);

  // Handle enabling debug mode
  const handleEnableDebug = () => {
    enableDebugBundle();
    // Force a re-render by causing the component to re-evaluate
    window.location.reload();
  };

  // Handle disabling debug mode
  const handleDisableDebug = () => {
    disableDebugBundle();
    // Force a re-render by causing the component to re-evaluate
    window.location.reload();
  };

  // Build the bundle (shared by all export methods)
  const getBundle = () => {
    // IMPORTANT: Use the STORED normalizedInput that was actually passed to compute,
    // not a fresh conversion. This ensures debug bundle shows the actual input used,
    // avoiding divergence between normalizedInput and computeOutput.inputEcho.
    // Fallback to fresh conversion only if stored input is unavailable.
    const normalizedInput = recommendationResult?.normalizedInput ??
                            (collectedData ? convertToSystemRecInput(collectedData) : null);
    return buildDebugBundle(
      collectedData,
      normalizedInput,
      recommendationResult,
      notes,
      location.pathname
    );
  };

  // Traditional download (may not work on iOS Safari)
  const handleDownload = () => {
    try {
      const bundle = getBundle();
      const filename = generateDebugBundleFilename();
      downloadJson(filename, bundle);
      alert('Debug bundle downloaded successfully!');
    } catch (error) {
      console.error('Failed to download debug bundle:', error);
      alert('Failed to download. Try "Copy JSON" instead.');
    }
  };

  // Copy to clipboard (works everywhere including iOS)
  const handleCopy = async () => {
    try {
      const bundle = getBundle();
      const json = JSON.stringify(bundle, null, 2);
      await navigator.clipboard.writeText(json);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      // Fallback: show in a textarea for manual copy
      const bundle = getBundle();
      const json = JSON.stringify(bundle, null, 2);
      const textarea = document.createElement('textarea');
      textarea.value = json;
      textarea.style.position = 'fixed';
      textarea.style.top = '50%';
      textarea.style.left = '50%';
      textarea.style.transform = 'translate(-50%, -50%)';
      textarea.style.width = '80vw';
      textarea.style.height = '60vh';
      textarea.style.zIndex = '10000';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      alert('JSON shown in textarea. Select all and copy manually.');
    }
  };

  // Share sheet (iOS-friendly)
  const handleShare = async () => {
    try {
      const bundle = getBundle();
      const json = JSON.stringify(bundle, null, 2);
      const filename = generateDebugBundleFilename();

      // Use Web Share API with file if available
      if (navigator.share && navigator.canShare) {
        const file = new File([json], filename, { type: 'application/json' });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: 'Debug Bundle',
            text: 'Atlas debug bundle'
          });
          return;
        }
      }

      // Fallback: share as text
      if (navigator.share) {
        await navigator.share({
          title: 'Debug Bundle',
          text: json
        });
        return;
      }

      // No share API: fall back to copy
      await handleCopy();
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        console.error('Share failed:', error);
        await handleCopy();
      }
    }
  };
  
  return (
    <div className={`debug-bundle-container ${className}`} style={{ display: 'flex', flexDirection: 'column', gap: '8px', ...style }}>
      {/* Build fingerprint - for deployment verification (only in full debug mode) */}
      {isFullDebugMode && (
        <div style={{ fontSize: '0.65rem', color: '#9ca3af', fontFamily: 'monospace' }}>
          Build: {BUILD_COMMIT.slice(0, 7)} @ {BUILD_TIME.slice(0, 16)}
        </div>
      )}

      {/* Notes section (only in full debug mode) */}
      {isFullDebugMode && showNotes && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label htmlFor="debug-notes" style={{ fontSize: '0.875rem', fontWeight: 500 }}>
            Notes (optional)
          </label>
          <textarea
            id="debug-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any notes about this debug session..."
            rows={3}
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              fontSize: '0.875rem',
              fontFamily: 'inherit',
              resize: 'vertical'
            }}
          />
        </div>
      )}
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
        {/* Dev toggle button - click to show/hide debug tools */}
        <button
          onClick={() => setDevToolsExpanded(!devToolsExpanded)}
          className="btn btn-link"
          style={{
            padding: '8px 12px',
            background: devToolsExpanded ? '#4b5563' : '#9ca3af',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '0.75rem',
            fontWeight: 500,
          }}
        >
          🔧 Dev
        </button>

        {/* Debug tools - only visible when expanded */}
        {devToolsExpanded && (
          <>
            {/* Primary: Copy JSON */}
            <button
              onClick={handleCopy}
              className="btn btn-primary"
              style={{
                padding: '8px 16px',
                background: copySuccess ? '#22c55e' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              {copySuccess ? '✓ Copied!' : '📋 Copy debug JSON'}
            </button>

            {/* Download */}
            <button
              onClick={handleDownload}
              className="btn btn-secondary"
              style={{
                padding: '8px 12px',
                background: '#6366f1',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              📦 Download JSON
            </button>
          </>
        )}

        {/* Share (iOS-friendly) - only when dev tools expanded and in full debug mode */}
        {devToolsExpanded && isFullDebugMode && typeof navigator !== 'undefined' && 'share' in navigator && (
          <button
            onClick={handleShare}
            className="btn btn-secondary"
            style={{
              padding: '8px 16px',
              background: '#8b5cf6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            📤 Share
          </button>
        )}

        {/* Notes toggle (only when dev tools expanded and in full debug mode) */}
        {devToolsExpanded && isFullDebugMode && (
          <button
            onClick={() => setShowNotes(!showNotes)}
            className="btn btn-link"
            style={{
              padding: '8px',
              background: 'transparent',
              color: '#6b7280',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.75rem'
            }}
          >
            {showNotes ? '−' : '+'} Notes
          </button>
        )}

        {/* Enable/Disable debug toggle - only when dev tools expanded */}
        {devToolsExpanded && (isFullDebugMode ? (
          <button
            onClick={handleDisableDebug}
            className="btn btn-link"
            style={{
              padding: '8px',
              background: 'transparent',
              color: '#ef4444',
              border: '1px solid #fca5a5',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.75rem',
              marginLeft: 'auto'
            }}
            title="Disable debug mode"
          >
            ✕ Disable
          </button>
        ) : (
          <button
            onClick={handleEnableDebug}
            className="btn btn-link"
            style={{
              padding: '8px',
              background: 'transparent',
              color: '#6b7280',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.75rem',
              marginLeft: 'auto'
            }}
            title="Enable full debug mode"
          >
            🔧 More
          </button>
        ))
        }
      </div>
    </div>
  );
}

// Export build info for use elsewhere
export const BUILD_INFO = { commit: BUILD_COMMIT, time: BUILD_TIME };
